import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, CircularProgress, Box, Typography } from '@mui/material';
import { AppProvider, useApp } from './contexts/AuthContext';
import { MainLayout } from './components/layout/MainLayout';
import { Dashboard } from './components/dashboard/DashboardEnhanced';
import { Scope12DataCollection } from './components/scope12/Scope12DataCollection';
import { Scope3Management } from './components/scope3/Scope3Management';
import { WaterDataCollection } from './components/environment/WaterDataCollection';
import { WasteDataCollection } from './components/environment/WasteDataCollection';
import { RenewableEnergyCollection } from './components/environment/RenewableEnergyCollection';
import { SocialDataCapture } from './components/social/SocialDataCapture';
import { AdminManagement } from './components/admin/AdminManagement';
import { EmissionFactorManagement } from './components/admin/EmissionFactorManagement';
import { AuditTrailViewer } from './components/admin/AuditTrailViewer';
import InsightsPage from './components/insights/InsightsPage';
import { GRIQuestionnaire } from './components/gri';
import { AuthPage, AuthCallback } from './components/auth';
import ErrorBoundary from './components/shared/ErrorBoundary';

const theme = createTheme({
  palette: {
    primary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    secondary: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    success: {
      main: '#22c55e',
      dark: '#16a34a',
    },
    warning: {
      main: '#f59e0b',
    },
    error: {
      main: '#ef4444',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h4: {
      fontSize: '1.75rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
    },
  },
});

// Protected route wrapper with error boundary for each route
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, needsSetup, logout } = useApp();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Users without an organization see a setup-pending message
  // instead of broken API calls that require an org context
  if (needsSetup) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" gutterBottom>
          Account Setup Pending
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480 }}>
          Your account is pending organization setup. Please contact your administrator
          to be assigned to an organization before you can access the platform.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <button
            onClick={() => logout()}
            style={{
              padding: '8px 24px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: '#3b82f6',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Sign Out
          </button>
        </Box>
      </Box>
    );
  }

  // Wrap each protected route in its own error boundary
  // so errors in one page don't crash the entire app
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

// App content with routing
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useApp();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <AuthPage />}
        />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/scope12"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Scope12DataCollection />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/scope3"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Scope3Management />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* Environment Routes */}
        <Route path="/water" element={<ProtectedRoute><MainLayout><WaterDataCollection /></MainLayout></ProtectedRoute>} />
        <Route path="/waste" element={<ProtectedRoute><MainLayout><WasteDataCollection /></MainLayout></ProtectedRoute>} />
        <Route path="/renewable" element={<ProtectedRoute><MainLayout><RenewableEnergyCollection /></MainLayout></ProtectedRoute>} />
        <Route path="/social" element={<ProtectedRoute><MainLayout><SocialDataCapture /></MainLayout></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><MainLayout><AdminManagement /></MainLayout></ProtectedRoute>} />
        <Route path="/admin/emission-factors" element={<ProtectedRoute><MainLayout><EmissionFactorManagement /></MainLayout></ProtectedRoute>} />
        <Route path="/admin/audit-trail" element={<ProtectedRoute><MainLayout><AuditTrailViewer /></MainLayout></ProtectedRoute>} />
        <Route path="/insights" element={<ProtectedRoute><MainLayout><InsightsPage /></MainLayout></ProtectedRoute>} />
        <Route path="/gri" element={<ProtectedRoute><MainLayout><GRIQuestionnaire /></MainLayout></ProtectedRoute>} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
